import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { Alert, Button, Field } from "../../../ui";
import type { FieldElement } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import { failure } from "../../workspace/behavior";
import "./BannerImageFields.css";

export interface BannerImageFieldsProps {
  readonly fileAssetId: number | null;
  readonly alt: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly altError?: string;
  readonly onChange?: (fileAssetId: number | null, alt: string) => void;
  readonly onBusyChange?: (busy: boolean) => void;
  readonly onUpload?: (file: File, signal: AbortSignal) => Promise<Schemas["FileOperationResponse"]>;
  readonly onLoadImage?: (fileAssetId: number, signal: AbortSignal) => Promise<Blob>;
}

/** Form composition of the existing Field, Alert and Button contracts. */
export function BannerImageFields(props: BannerImageFieldsProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [previewState, setPreviewState] = useState<"loading" | "ready" | "error">("loading");
  const [retry, setRetry] = useState(0);
  const uploadRequest = useRef<AbortController | null>(null);
  const fileInput = useRef<FieldElement | null>(null);
  const localImage = useRef<{ id: number; file: File } | null>(null);

  useEffect(() => () => uploadRequest.current?.abort(), []);
  useEffect(() => {
    const request = new AbortController();
    let url: string | undefined;
    setPreviewUrl(undefined);
    if (!props.fileAssetId) return;
    setPreviewState("loading");
    const id = props.fileAssetId;
    const load = async () => {
      try {
        const blob = localImage.current?.id === id ? localImage.current.file
          : await props.onLoadImage?.(id, request.signal);
        if (request.signal.aborted) return;
        if (!blob || !["image/png", "image/jpeg", "image/webp"].includes(blob.type)) throw new Error("Unsupported image.");
        url = URL.createObjectURL(blob);
        const decoded = new Image();
        decoded.src = url;
        if (typeof decoded.decode === "function") await decoded.decode();
        if (request.signal.aborted) return;
        setPreviewUrl(url);
        setPreviewState("ready");
      } catch {
        if (!request.signal.aborted) setPreviewState("error");
      }
    };
    void load();
    return () => { request.abort(); if (url) URL.revokeObjectURL(url); };
  }, [props.fileAssetId, props.onLoadImage, retry]);

  const upload = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file || uploading || uploadRequest.current || props.disabled || !props.onUpload) return;
    setUploadError("");
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size < 1 || file.size > 20_971_520) {
      setUploadError("20MB 이하의 PNG, JPEG 또는 WebP 이미지를 선택해 주세요.");
      return;
    }
    const request = new AbortController();
    uploadRequest.current = request;
    setUploading(true);
    props.onBusyChange?.(true);
    try {
      const result = await props.onUpload(file, request.signal);
      if (request.signal.aborted) return;
      if (result.status !== "READY" || !result.fileAssetId || !Number.isSafeInteger(result.fileAssetId) || result.fileAssetId < 1) throw new Error("이미지 검사가 완료되지 않았습니다. 다시 업로드해 주세요.");
      localImage.current = { id: result.fileAssetId, file };
      props.onChange?.(result.fileAssetId, props.alt);
    } catch (cause) {
      if (!request.signal.aborted) setUploadError(failure(cause).message);
    } finally {
      uploadRequest.current = null;
      if (!request.signal.aborted) { setUploading(false); props.onBusyChange?.(false); }
    }
  };

  return <fieldset className="admin-command__fieldset admin-banner-image">
    <legend>가입 화면 이미지</legend>
    {!props.readOnly && <>
      <Field ref={fileInput} compact type="file" label={props.fileAssetId ? "이미지 교체" : "이미지 등록"} accept="image/png,image/jpeg,image/webp" disabled={props.disabled || uploading || !props.onUpload} description="PNG, JPEG, WebP · 최대 20MB. 저장하면 해당 배너의 가입 화면에 적용됩니다." onChange={upload}/>
      {uploading && <Alert persistent title="이미지를 업로드·검사하는 중입니다">검사가 끝날 때까지 저장할 수 없습니다. 교체 중인 기존 이미지는 유지됩니다.</Alert>}
      {uploadError && <Alert persistent variant="danger" title="이미지를 등록하지 못했습니다">{uploadError} 기존 이미지 선택은 유지됩니다.</Alert>}
      <Field compact label="이미지 설명" maxLength={500} value={props.alt} disabled={!props.fileAssetId || props.disabled || uploading} error={props.altError} description="이미지가 전달하는 내용을 설명해 주세요. 화면을 읽어주는 도구에서 사용합니다." onChange={event => props.onChange?.(props.fileAssetId, event.currentTarget.value)}/>
    </>}
    {!props.fileAssetId ? <p>등록된 이미지가 없습니다. 가입 화면의 광고 영역은 표시되지 않습니다.</p>
      : previewState === "loading" ? <p role="status">이미지 미리보기를 불러오는 중입니다.</p>
        : previewState === "error" ? <Alert persistent variant="warning" title="이미지 미리보기를 불러오지 못했습니다" action={<Button size="small" variant="quiet" disabled={uploading} onClick={() => setRetry(value => value + 1)}>다시 보기</Button>}>이미지 열람 권한과 연결 상태를 확인해 주세요. 이미지 선택은 유지됩니다.</Alert>
          : <img className="admin-banner-image__preview" src={previewUrl} alt={props.alt || "가입 화면 이미지 미리보기"} onError={() => setPreviewState("error")}/>}
    {!props.readOnly && props.fileAssetId && <Button size="small" variant="quiet" type="button" disabled={props.disabled || uploading} onClick={() => { setUploadError(""); localImage.current = null; props.onChange?.(null, ""); fileInput.current?.focus(); }}>이미지 제거</Button>}
    {!props.readOnly && <p>이미지를 제거해도 가입 링크는 유지됩니다. 배너 활성 상태는 별도로 관리합니다.</p>}
  </fieldset>;
}
